<script setup>
import axios from "axios";
import { createExamClock } from "@/composables/examClock";
import { useExamAnswers } from "@/composables/useExamAnswers";
import { onMounted, ref, computed, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import Dialog from "primevue/dialog";

const route  = useRoute();
const router = useRouter();
const examId = route.params.id;

/* ─── Dialog states ─── */
const questionImageVisible = ref(false);
const answerImageVisible   = ref(false);
const selectedImage = ref(null);
const selectedTitle = ref(null);

/* ─── State ─── */
const loading    = ref(true);
const submitting = ref(false);
const exam       = ref({});
const answers    = ref({});
const currentQuestionIndex = ref(0);
const attempt    = ref(null);
const timeLeft   = ref(null);
const examEndMs = ref(null);
const synchronizedClock = createExamClock();
let timerInterval = null;

const examType = ref(route.query.type || "homework");

const { answerLocks, answerFeedback, textAnswers, textDrafts, savingAnswer,
  hydrateAnswerState, selectAnswer, saveTextAnswer, waitForAnswerSave, optionClasses,
} = useExamAnswers({ exam, attempt, answers, loading, submitting });


/* ─── Helpers ─── */
const openAnswerImage = (src, title) => {
  selectedImage.value = src;
  selectedTitle.value = title;
  answerImageVisible.value = true;
};
watch(answerImageVisible, (v) => { if (!v) { selectedImage.value = null; selectedTitle.value = null; } });

const updateTimeLeft = () => {
  timeLeft.value = synchronizedClock.remaining(examEndMs.value);
  if (timeLeft.value === 0) forceSubmitExam(true);
};

const buildAnswersPayload = () =>
    exam.value.questions.map((q) => ({
      question_id: q.id,
      answer_id: answers.value[q.id] ?? null,
      text_answer: textAnswers.value[q.id] ?? null,
    }));

/* ─── API ─── */
const fetchExam = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`/exam_web/${examId}`);
    if (!res.data.status) throw new Error(res.data.message);
    exam.value    = res.data.exam;
    attempt.value = res.data.attempt;
    // The API returns [] for an empty map; keep question-ID keys in an object
    // so answering a lower ID later in a random order remains reactive.
    answers.value = { ...(res.data.saved_answers || {}) };
    hydrateAnswerState(res.data);
    // Sequential modes: resume at the current question (the frontier = first
    // unanswered in the already-ordered list), so a refresh/return reconstructs the
    // reached position from the persisted answers instead of resetting to index 0.
    // Free mode always starts at the first.
    if (requiresSequential.value) {
      currentQuestionIndex.value = frontier.value;
    }
    // Hydrate saved answers without posting them again as new edits.
    // Reuse the existing start route and the server-provided effective deadline.
    try { await axios.post('/exams/' + examId + '/start'); } catch (_) { /* GET already started personal attempts. */ }
    if (res.data.exam_end_at && res.data.server_now) {
      synchronizedClock.sync(res.data.server_now);
      examEndMs.value = Date.parse(res.data.effective_exam_end_at || res.data.exam_end_at);
      if (timerInterval) clearInterval(timerInterval);
      updateTimeLeft();
      timerInterval = setInterval(updateTimeLeft, 1000);
    }
  } catch (e) {
    Swal.fire("غير مسموح", e.response?.data?.message || e.message, "error")
        .then(() => router.push(`/exams?type=${examType.value}`));
  } finally {
    loading.value = false;
  }
};

// Answer selection/locking/feedback is shared by all three student applications.

/* ─── Submit ─── */
const forceSubmitExam = async (auto = false) => {
  if (submitting.value) return;
  submitting.value = true;
  try {
    await waitForAnswerSave();
    const res = await axios.post("/exam-submit", {
      exam_id: exam.value.id, attempt: attempt.value,
      answers: buildAnswersPayload(),
      submit_reason: auto ? "timeout" : "manual",
    });
    if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
    const d = res.data.data;
    await Swal.fire({
      title: auto ? "انتهى الوقت ⏰" : "تم التسليم ✅",
      icon: d.results_available === false ? "success" : d.percentage >= 60 ? "success" : "error",
      html: d.results_available === false ? "<p>تم حفظ التسليم. الإجابات والنتيجة غير متاحة للعرض حالياً.</p>" : `<p style="font-family:Cairo,sans-serif;font-size:1.3rem;margin:.5rem 0"><strong>${d.student_score} / ${d.exam_total_score}</strong></p>
             <p style="font-family:Cairo,sans-serif;font-size:1.5rem;font-weight:800">${d.percentage}%</p>`,
      confirmButtonText: "العودة",
      allowOutsideClick: false,
    });
    router.push(`/exams?type=${examType.value}`);
  } catch {
    submitting.value = false;
    Swal.fire("خطأ", "فشل التسليم", "error");
  }
};

const submitExam = async () => {
  const c = await Swal.fire({
    title: "تأكيد التسليم",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "تسليم الامتحان",
    cancelButtonText: "رجوع",
  });
  if (c.isConfirmed) forceSubmitExam();
};

/* ─── Computed ─── */
const currentQuestion = computed(() => exam.value.questions?.[currentQuestionIndex.value]);
// Navigation mode (from the exam setting). null/legacy → free. The backend also
// enforces all of this — the UI just mirrors it.
//   sequential      → forward-only, must-answer, no going back, no jumping.
//   sequential_back → forward one-at-a-time, must-answer, MAY revisit reached, no jumping ahead.
//   free            → unrestricted.
const isSequential     = computed(() => exam.value?.navigation_mode === 'sequential');
const isSequentialBack = computed(() => exam.value?.navigation_mode === 'sequential_back');
const requiresSequential = computed(() => isSequential.value || isSequentialBack.value);
// The frontier = index of the first unanswered question in the ordered list (the
// furthest reached position). Everything up to and including it has been reached;
// anything beyond it is not yet reachable. If all answered → the last index.
const frontier = computed(() => {
  const qs = exam.value?.questions || [];
  const idx = qs.findIndex(q => !isAnswered(q.id));
  return idx === -1 ? Math.max(0, qs.length - 1) : idx;
});
const currentAnswered = computed(() => !!currentQuestion.value && isAnswered(currentQuestion.value.id));
const answeredCount = computed(() => (exam.value.questions || []).filter(q => isAnswered(q.id)).length);
const progress        = computed(() =>
    exam.value.questions ? ((currentQuestionIndex.value + 1) / exam.value.questions.length) * 100 : 0
);
const formattedTime   = computed(() => {
  if (timeLeft.value === null) return "";
  const m = String(Math.floor(timeLeft.value / 60)).padStart(2, "0");
  const s = String(timeLeft.value % 60).padStart(2, "0");
  return `${m}:${s}`;
});
const timerDanger = computed(() => timeLeft.value !== null && timeLeft.value < 120);

/* ─── Navigation ─── */
const isAnswered = (id) => (answers.value[id] !== undefined && answers.value[id] !== null) || !!textAnswers.value[id];
const nextQuestion = () => {
  if (currentQuestionIndex.value >= exam.value.questions.length - 1) return;
  // Both sequential modes advance one at a time and only after the current
  // question is answered (which is exactly what pushes the frontier forward).
  if (savingAnswer.value || (requiresSequential.value && !currentAnswered.value)) return;
  currentQuestionIndex.value++;
};
const prevQuestion = () => {
  // Strict sequential: no going back at all. sequential_back/free: previous allowed.
  if (savingAnswer.value || isSequential.value) return;
  if (currentQuestionIndex.value > 0) currentQuestionIndex.value--;
};
const goToQuestion = (i) => {
  // Strict sequential: no jumping via the palette at all.
  if (savingAnswer.value || isSequential.value) return;
  // sequential_back: may jump only to an already-reached question (index <= frontier).
  if (isSequentialBack.value && i > frontier.value) return;
  currentQuestionIndex.value = i;
};

/* ─── Lifecycle ─── */
let lastResync = 0;
const resyncAttempt = async () => {
  if (document.visibilityState !== 'visible' || loading.value || submitting.value || savingAnswer.value) return;
  const mono = performance.now();
  if (mono - lastResync < 1500) return;
  lastResync = mono;
  try {
    const { data } = await axios.get('/exam_web/' + examId);
    if (data.completed) { fetchExam(); return; }
    hydrateAnswerState(data, true);
    synchronizedClock.sync(data.server_now);
    examEndMs.value = data.effective_exam_end_at ? Date.parse(data.effective_exam_end_at) : null;
    updateTimeLeft();
  } catch (error) {
    if (['exam_expired', 'EXAM_ENDED'].includes(error.response?.data?.code)) forceSubmitExam(true);
  }
};
onMounted(() => {
  fetchExam();
  document.addEventListener('visibilitychange', resyncAttempt);
  window.addEventListener('focus', resyncAttempt);
  window.addEventListener('pageshow', resyncAttempt);
});
onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
  document.removeEventListener('visibilitychange', resyncAttempt);
  window.removeEventListener('focus', resyncAttempt);
  window.removeEventListener('pageshow', resyncAttempt);
});
</script>

<template>
  <!-- Loading -->
  <div v-if="loading" class="exam-loading">
    <div class="exam-spinner"></div>
    <p>جاري تحميل الامتحان...</p>
  </div>

  <!-- Exam UI -->
  <div v-else-if="exam?.questions" class="exam-wrapper">

    <!-- ── Sticky Timer ── -->
    <div v-if="timeLeft !== null" class="timer-bar" :class="{ 'timer-bar--danger': timerDanger }">
      <span class="timer-label">⏱ الوقت المتبقي</span>
      <span class="timer-value">{{ formattedTime }}</span>
      <div class="timer-track">
        <div
          class="timer-fill"
          :style="{ width: (timeLeft / (exam.to_time ? 3600 : 1)) * 100 + '%' }"
        ></div>
      </div>
    </div>

    <div class="exam-container">

      <!-- ── Exam header ── -->
      <div class="exam-header">
        <div class="exam-header-badge">{{ examType === 'online' ? '📝 امتحان' : '🏠 واجب' }}</div>
        <h1>{{ exam.title }}</h1>
        <p class="exam-lesson">{{ exam.lesson?.title || "" }}</p>
        <div class="exam-meta">
          <span>🎯 الدرجة الكلية: <strong>{{ exam.total_degree }}</strong></span>
          <span v-if="attempt">• المحاولة {{ attempt }}</span>
          <span v-if="exam.personal_duration_minutes">• المدة الشخصية: {{ exam.personal_duration_minutes }} دقيقة (بحد أقصى نهاية الوقت العام)</span>
        </div>
      </div>

      <!-- ── Progress bar ── -->
      <div class="prog-wrap">
        <div class="prog-bar">
          <div class="prog-fill" :style="{ width: progress + '%' }"></div>
        </div>
        <div class="prog-text">
          <span>{{ answeredCount }} / {{ exam.questions.length }} مجاب</span>
          <span>سؤال {{ currentQuestionIndex + 1 }} من {{ exam.questions.length }}</span>
        </div>
      </div>

      <!-- ── Question card ── -->
      <div class="question-card" v-if="currentQuestion">
        <div class="q-num-badge">#{{ currentQuestionIndex + 1 }}</div>

        <h2 class="question-title">{{ currentQuestion.title }}</h2>

        <img
          v-if="currentQuestion.image"
          :src="currentQuestion.image"
          class="question-img"
          @click="questionImageVisible = true"
          title="اضغط لتكبير الصورة"
        />

        <Dialog
          v-model:visible="questionImageVisible"
          modal header="صورة السؤال"
          :style="{ width: '80vw' }"
          :breakpoints="{ '85vw': '85vw', '575px': '92vw' }"
        >
          <img :src="currentQuestion.image" style="width:100%;border-radius:12px" />
        </Dialog>

        <p v-if="answerLocks[currentQuestion.id]" class="answer-lock-note" role="status">🔒 تم تثبيت الإجابة ولا يمكن تغييرها.</p>
        <p v-if="answerFeedback[currentQuestion.id]" class="answer-feedback-note" role="status"
          :class="answerFeedback[currentQuestion.id].is_correct ? 'feedback-correct' : 'feedback-incorrect'">
          {{ answerFeedback[currentQuestion.id].is_correct ? 'إجابة صحيحة ✓' : 'إجابة غير صحيحة — الإجابة الصحيحة موضحة بالأخضر.' }}
        </p>
        <div v-if="currentQuestion.type === 'short_answer'" class="short-answer">
          <label :for="'text-answer-' + currentQuestion.id">إجابتك</label>
          <textarea :id="'text-answer-' + currentQuestion.id" v-model="textDrafts[currentQuestion.id]"
            :disabled="savingAnswer || submitting || answerLocks[currentQuestion.id] || timeLeft === 0" />
          <button type="button" @click="saveTextAnswer(currentQuestion.id)"
            :disabled="savingAnswer || submitting || answerLocks[currentQuestion.id] || timeLeft === 0">حفظ الإجابة</button>
        </div>
        <!-- Answers -->
        <div class="answers-list">
          <label
            v-for="a in currentQuestion.answers"
            :key="a.id"
            class="answer-option"
            :class="optionClasses(currentQuestion.id, a.id)"
          >
            <input
              type="radio"
              :name="'q_' + currentQuestion.id"
              :value="a.id"
              :checked="answers[currentQuestion.id] === a.id"
              @change="selectAnswer(currentQuestion.id, a.id)"
              :disabled="savingAnswer || submitting || answerLocks[currentQuestion.id] || timeLeft === 0"
            />
            <span class="answer-radio"></span>
            <div class="answer-body">
              <span class="answer-text">{{ a.title }}</span>
              <img
                v-if="a.image"
                :src="a.image"
                class="answer-img"
                @click.stop="openAnswerImage(a.image, a.title)"
              />
            </div>
          </label>
        </div>

        <!-- Answered hint -->
        <div v-if="isAnswered(currentQuestion.id)" class="answered-hint">
          ✓ تم الإجابة على هذا السؤال
        </div>
      </div>

      <!-- ── Navigation ── -->
      <div class="exam-nav">
        <!-- Strict sequential hides "السابق" (no going back). free & sequential_back keep it. -->
        <button v-if="!isSequential" @click="prevQuestion" :disabled="savingAnswer || currentQuestionIndex === 0" class="nav-btn nav-btn--prev">
          ← السابق
        </button>

        <button
          v-if="currentQuestionIndex === exam.questions.length - 1"
          @click="submitExam"
          :disabled="submitting || savingAnswer"
          class="nav-btn nav-btn--submit"
        >
          {{ submitting ? "جاري التسليم..." : "✅ تسليم الامتحان" }}
        </button>
        <!-- Both sequential modes disable "التالي" until the current question is answered. -->
        <button v-else @click="nextQuestion" :disabled="savingAnswer || (requiresSequential && !currentAnswered)" class="nav-btn nav-btn--next">
          التالي →
        </button>
      </div>

      <!-- ── Question palette ── -->
      <div class="palette-wrap">
        <p class="palette-title">لوحة الأسئلة</p>
        <div class="palette">
          <button
            v-for="(q, i) in exam.questions"
            :key="q.id"
            @click="goToQuestion(i)"
            :disabled="savingAnswer || isSequential || (isSequentialBack && i > frontier)"
            :class="{
              'palette-btn--active':    i === currentQuestionIndex,
              'palette-btn--answered':  isAnswered(q.id),
            }"
            class="palette-btn"
          >{{ i + 1 }}</button>
        </div>
        <div class="palette-legend">
          <span class="legend-dot answered"></span>تم الإجابة
          <span class="legend-dot current" style="margin-right:1rem"></span>الحالي
          <span class="legend-dot empty" style="margin-right:1rem"></span>لم يُجَب
        </div>
      </div>

    </div>

    <!-- Answer image dialog -->
    <Dialog
      v-model:visible="answerImageVisible"
      modal header="الصورة"
      :dismissableMask="true"
      :style="{ width: '80vw' }"
      :breakpoints="{ '85vw': '85vw', '575px': '92vw' }"
    >
      <p style="font-family:Cairo,sans-serif;font-weight:600;margin-bottom:.75rem">{{ selectedTitle }}</p>
      <img
        v-if="selectedImage"
        :src="selectedImage"
        style="max-width:100%;max-height:80vh;object-fit:contain;border-radius:12px"
      />
    </Dialog>

  </div>

  <!-- No exam -->
  <div v-else class="exam-empty">
    <div style="font-size:4rem;margin-bottom:1rem">📋</div>
    <p>لا يوجد امتحان متاح حالياً</p>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.exam-wrapper {
  background: #0D0D0D; direction: rtl;
  min-height: calc(100vh - 70px);
  font-family: 'Cairo', sans-serif;
  padding-bottom: 3rem;
}

/* ── Timer bar ── */
.timer-bar {
  position: sticky; top: 70px; z-index: 100;
  background: rgba(15,15,15,.97); backdrop-filter: blur(10px);
  border-bottom: 2px solid rgba(212,175,55,.2);
  padding: .6rem 1.5rem;
  display: flex; align-items: center; gap: 1rem;
  flex-wrap: wrap; box-shadow: 0 4px 16px rgba(0,0,0,.06);
}
.timer-label { font-weight: 600; color: rgba(240,237,228,0.65); font-size: .88rem; }
.timer-value {
  font-size: 1.4rem; font-weight: 800; color: #D4AF37;
  font-family: 'Cairo', sans-serif;
  min-width: 70px; text-align: center;
  background: rgba(212,175,55,.08); padding: .2rem .75rem;
  border-radius: 8px;
}
.timer-track {
  flex: 1; height: 6px; background: rgba(212,175,55,.12);
  border-radius: 99px; overflow: hidden; min-width: 80px;
}
.timer-fill { height: 100%; background: linear-gradient(90deg,#D4AF37,#C89B2D); border-radius: 99px; transition: width 1s linear; }
.timer-bar--danger .timer-value { color: #EF4444; background: rgba(239,68,68,.1); animation: blink 1s infinite; }
.timer-bar--danger .timer-fill  { background: linear-gradient(90deg,#EF4444,#dc2626); }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:.5} }

/* ── Container ── */
.exam-container {
  max-width: 820px; margin: 1.5rem auto;
  padding: 0 1.25rem;
  display: flex; flex-direction: column; gap: 1.5rem;
}

/* ── Exam header ── */
.exam-header {
  text-align: center; padding: 2rem 1.5rem;
  background: linear-gradient(135deg,#D4AF37 0%,#C89B2D 100%);
  border-radius: 20px; color: #fff;
  box-shadow: 0 12px 32px rgba(212,175,55,.3);
}
.exam-header-badge {
  display: inline-block; background: rgba(255,255,255,.2);
  border: 1px solid rgba(255,255,255,.3);
  border-radius: 99px; padding: .25rem .9rem;
  font-size: .8rem; font-weight: 700; margin-bottom: .9rem;
}
.exam-header h1 { font-size: 1.9rem; font-weight: 800; margin-bottom: .5rem; }
.exam-lesson { font-size: 1rem; opacity: .9; margin-bottom: .75rem; }
.exam-meta { font-size: .9rem; opacity: .85; display: flex; justify-content: center; gap: .75rem; flex-wrap: wrap; }

/* ── Progress ── */
.prog-wrap { background: #fff; border-radius: 16px; padding: 1rem 1.25rem; box-shadow: 0 4px 12px rgba(0,0,0,.06); }
.prog-bar { height: 10px; background: rgba(212,175,55,.1); border-radius: 99px; overflow: hidden; margin-bottom: .65rem; }
.prog-fill { height: 100%; background: linear-gradient(90deg,#D4AF37,#C89B2D); border-radius: 99px; transition: width .5s ease; }
.prog-text { display: flex; justify-content: space-between; font-size: .82rem; color: #6B7280; font-weight: 600; }

/* ── Question card ── */
.question-card {
  background: #fff; border-radius: 20px; padding: 2rem;
  box-shadow: 0 8px 24px rgba(0,0,0,.07);
  border: 2px solid rgba(212,175,55,.08);
  position: relative;
}
.q-num-badge {
  position: absolute; top: -16px; right: 1.5rem;
  background: linear-gradient(135deg,#D4AF37,#C89B2D);
  color: #fff; width: 48px; height: 48px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; font-weight: 800;
  box-shadow: 0 8px 20px rgba(212,175,55,.35);
}
.question-title { font-size: 1.45rem; font-weight: 700; color: #1A1A1A; margin-bottom: 1rem; line-height: 1.7; padding-top: .5rem; }
.question-img {
  display: block; max-width: 65%; margin: 0 0 1.25rem;
  border-radius: 12px; cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,.1);
  transition: transform .2s; border: 2px solid rgba(212,175,55,.1);
}
.question-img:hover { transform: scale(1.02); }

/* Answers */
.answers-list { display: flex; flex-direction: column; gap: 1rem; }
.answer-option {
  display: flex; align-items: flex-start; gap: 1rem;
  padding: 1.1rem 1.25rem; background: #F8FAFC;
  border-radius: 14px; border: 2px solid #E2E8F0;
  cursor: pointer; transition: all .25s ease;
}
.answer-option:hover { border-color: #D4A830; transform: translateX(-4px); box-shadow: 0 6px 20px rgba(212,175,55,.12); }
.answer-option--selected {
  border-color: #D4AF37; background: #EEF2FF;
  box-shadow: 0 8px 24px rgba(212,175,55,.18);
}
.answer-option input { position: absolute; opacity: 0; pointer-events: none; }
.answer-radio {
  width: 22px; height: 22px; flex-shrink: 0;
  border: 2.5px solid #CBD5E1; border-radius: 50%;
  transition: all .25s; margin-top: 2px;
  display: flex; align-items: center; justify-content: center;
}
.answer-option--selected .answer-radio {
  background: #D4AF37; border-color: #D4AF37;
  box-shadow: 0 0 0 4px rgba(212,175,55,.2);
}
.answer-option--selected .answer-radio::after {
  content: ''; display: block; width: 7px; height: 7px;
  background: #fff; border-radius: 50%;
}
.answer-body { flex: 1; }
.answer-text { font-size: 1.05rem; color: #334155; font-weight: 500; line-height: 1.5; display: block; }
.answer-img {
  display: block; max-width: 55%; margin-top: .75rem;
  border-radius: 10px; cursor: pointer; transition: transform .2s;
  border: 2px solid rgba(212,175,55,.1);
}
.answer-img:hover { transform: scale(1.02); }

.answered-hint {
  margin-top: 1rem; text-align: center; color: #16a34a;
  font-weight: 700; background: rgba(34,197,94,.08);
  padding: .75rem; border-radius: 12px; font-size: .9rem;
}

/* ── Navigation ── */
.exam-nav {
  display: flex; justify-content: space-between;
  align-items: center; gap: 1rem; flex-wrap: wrap;
}
.nav-btn {
  padding: .85rem 2rem; border: none; border-radius: 14px;
  font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: all .3s; font-family: 'Cairo', sans-serif;
}
.nav-btn--prev { background: #F1F5F9; color: #475569; }
.nav-btn--prev:hover:not(:disabled) { background: #E2E8F0; transform: translateX(4px); }
.nav-btn--next { background: linear-gradient(135deg,#D4AF37,#C89B2D); color: #fff; box-shadow: 0 8px 20px rgba(212,175,55,.25); }
.nav-btn--next:hover:not(:disabled) { transform: translateX(-4px); box-shadow: 0 14px 30px rgba(212,175,55,.4); }
.nav-btn--submit { background: linear-gradient(135deg,#22C55E,#16a34a); color: #fff; font-size: 1.1rem; box-shadow: 0 8px 20px rgba(34,197,94,.3); }
.nav-btn--submit:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 16px 35px rgba(34,197,94,.45); }
.nav-btn:disabled { opacity: .45; cursor: not-allowed; transform: none !important; }

/* ── Palette ── */
.palette-wrap {
  background: #fff; border-radius: 20px; padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,.06);
}
.palette-title { font-weight: 700; color: #1A1A1A; margin-bottom: 1rem; font-size: .95rem; }
.palette { display: flex; flex-wrap: wrap; gap: .6rem; margin-bottom: 1rem; }
.palette-btn {
  width: 42px; height: 42px; border-radius: 50%; border: 2px solid #E2E8F0;
  background: #F8FAFC; color: #64748B; font-weight: 700; font-size: .9rem;
  cursor: pointer; transition: all .25s; font-family: 'Cairo', sans-serif;
}
.palette-btn:hover { border-color: #D4A830; color: #D4AF37; background: rgba(212,175,55,.06); }
.palette-btn--answered { background: #22C55E; border-color: #22C55E; color: #fff; }
.palette-btn--active   { background: #D4AF37; border-color: #D4AF37; color: #fff; transform: scale(1.2); box-shadow: 0 6px 16px rgba(212,175,55,.4); }
.palette-legend { display: flex; align-items: center; gap: .35rem; font-size: .78rem; color: #6B7280; flex-wrap: wrap; }
.legend-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
.answered { background: #22C55E; }
.current  { background: #D4AF37; }
.empty    { background: #E2E8F0; border: 1px solid #CBD5E1; }

/* ── Loading / empty ── */
.exam-loading, .exam-empty {
  text-align: center; padding: 6rem 1rem;
  font-family: 'Cairo', sans-serif; color: #6B7280; font-size: 1.2rem;
  background: #F5F7FB; min-height: calc(100vh - 70px);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.exam-spinner {
  width: 64px; height: 64px; border: 5px solid rgba(212,175,55,.15);
  border-top-color: #D4AF37; border-radius: 50%;
  animation: spin 1s linear infinite; margin-bottom: 1.5rem;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media(max-width:600px) {
  .exam-header h1 { font-size: 1.45rem; }
  .question-title { font-size: 1.2rem; }
  .question-img, .answer-img { max-width: 100%; }
  .exam-nav { flex-direction: column; }
  .nav-btn { width: 100%; text-align: center; }
  .timer-bar { top: 0; }
}

.answer-option--correct { border-color: #16a34a !important; background: #dcfce7 !important; color: #14532d !important; }
.answer-option--incorrect { border-color: #dc2626 !important; background: #fee2e2 !important; color: #7f1d1d !important; }
.answer-option--correct .answer-text, .answer-option--incorrect .answer-text { color: inherit !important; }
.answer-lock-note { padding: .75rem; border: 1px solid #94a3b8; border-radius: .75rem; color: #334155; background: #f1f5f9; }
.feedback-correct { color: #16a34a; }
.feedback-incorrect { color: #dc2626; }
.short-answer textarea { display: block; width: 100%; min-height: 6rem; margin: .5rem 0; }
</style>
